<template>
  <div class="auction-top">
    <div class="toolbar">
      <el-date-picker
        v-model="tradeDate"
        type="date"
        size="small"
        value-format="yyyy-MM-dd"
        placeholder="竞价日"
        style="width: 150px"
      />
      <el-button type="primary" size="small" :loading="loading" @click="handleQuery">查询</el-button>
      <el-button size="small" :loading="nextDayLoading" :disabled="!tradeDate" @click="handleNextTradeDay">
        下一日
      </el-button>
      <el-button size="small" :loading="libLoading" :disabled="!tradeDate" @click="openYesterdayTopLib">
        昨日TOP库
      </el-button>
      <el-checkbox v-model="filterMa250">过滤MA250</el-checkbox>
      <el-checkbox v-model="filterSlope">分段斜率剔除 &gt; 10%</el-checkbox>
      <el-select
        v-model="filterChipTier"
        size="small"
        clearable
        placeholder="筹码过滤"
        style="width: 160px"
      >
        <el-option label="T1 6%/6%/6%" value="T1" />
        <el-option label="T2 11%/13%/13%" value="T2" />
      </el-select>
      <el-select
        v-model="filterBoardLevels"
        size="small"
        multiple
        collapse-tags
        clearable
        placeholder="板位筛选"
        style="width: 160px"
      >
        <el-option label="首板" :value="1" />
        <el-option label="二板" :value="2" />
        <el-option label="三板" :value="3" />
      </el-select>
      <el-checkbox v-model="filterBaoTouch">爆量摸板</el-checkbox>
      <el-checkbox v-model="filterCarry2bFloor">2B大单承接比≥-0.55</el-checkbox>
      <el-checkbox v-model="filterCarry3bFloor">3B大单承接比≥-0.55</el-checkbox>
      <span v-if="prevDate" class="meta-tip">昨日 {{ prevDate }} · 共 {{ rows.length }} 只</span>
    </div>

    <div class="table-wrap">
      <el-table
        v-loading="loading"
        :data="rows"
        border
        size="mini"
        height="100%"
        class="auction-top-table"
        empty-text="暂无数据"
        :default-sort="{ prop: 'totalScore', order: 'descending' }"
      >
        <el-table-column label="标签" min-width="52" align="center">
          <template slot-scope="{ row }">
            <div v-if="row.topFlags > 0 || row.starTier || hasNodeCore(row)" class="top-cell">
              <div v-if="row.topFlags > 0" class="top-flags">{{ topFlagsText(row.topFlags) }}</div>
              <div v-if="row.starTier === 'pink5'" class="stars-pink stars-stack">
                <div class="stars-row">★★</div>
                <div class="stars-row">★★★</div>
              </div>
              <div v-else-if="row.starTier === 'blue3'" class="stars-blue stars-stack">
                <div class="stars-row">★★★</div>
              </div>
              <span
                v-if="hasNodeCore(row)"
                class="node-mark"
                title="首板日节点达标（V5F ≥7）"
              >⬡</span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" min-width="96" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="name-line">
              <span
                class="stock-name"
                :class="{ 'stock-name-hit': isNodeScoreHit(row.nodeScore) }"
                :style="nodeScoreColorStyle(row.nodeScore)"
              >{{ row.name || '-' }}</span><span
                v-if="yesterdayBoardGlyph(row)"
                class="board-glyph"
                :class="yesterdayBoardGlyphClass(row)"
              >{{ yesterdayBoardGlyph(row) }}</span>
            </div>
            <div class="code-sub">{{ row.code }}</div>
          </template>
        </el-table-column>
        <el-table-column
          prop="totalScore"
          label="综合分"
          min-width="64"
          align="center"
          sortable
          :sort-method="sortTotal"
        >
          <template slot-scope="{ row }">{{ row.totalScore != null ? row.totalScore : '-' }}</template>
        </el-table-column>
        <el-table-column
          label="逻辑"
          class-name="auction-logic-col"
          min-width="148"
        >
          <template slot-scope="{ row }">
            <div class="logic-cell">
              <span
                v-if="hasLogicMatch(row)"
                class="logic-cell__dot"
                :title="logicSourceTip"
              ></span>
              <div v-if="getLogicLines(row).length" class="logic-cell__lines">
                <div
                  v-for="(line, idx) in getLogicLines(row)"
                  :key="idx"
                  class="logic-cell__line"
                >
                  <span v-if="line.kind === 'switch'" class="logic-cell__text">切换</span>
                  <template v-else>
                    <span class="logic-cell__text">{{ line.label }}</span>
                    <span
                      v-if="line.gain != null"
                      class="logic-cell__gain"
                      :class="logicGainClass(line.gain)"
                    >{{ formatLogicGain(line.gain) }}</span>
                  </template>
                </div>
              </div>
              <span v-else class="logic-cell__text">—</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="今日爆量" min-width="118" align="center">
          <template slot-scope="{ row }">
            <div class="burst-tags" :class="{ 'is-both': bothBurst(row) }">
              <span
                v-if="row.auctionVolumeBurst === '爆'"
                class="auction-burst"
                :class="{ 'is-both': bothBurst(row) }"
              >爆</span>
              <span
                v-if="row.volume931Burst === '31分爆'"
                class="auction-burst"
                :class="{ 'is-both': bothBurst(row) }"
              >31分爆</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="今日竞价量" min-width="80" align="center">
          <template slot-scope="{ row }">{{ fmtVolumeWan(row.auctionVolumeToday) }}</template>
        </el-table-column>
        <el-table-column min-width="96" align="center">
          <template slot="header">
            <div class="th-stack">
              <div>历史</div>
              <div>竞价最大量</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span
              v-if="row.auctionVolume925MaxDate"
              @mouseenter="showAuctionMaxTip($event, row)"
              @mouseleave="hideAuctionMaxTip"
            >{{ fmtVolumeWan(row.auctionVolume925Max) }}</span>
            <span v-else>{{ fmtVolumeWan(row.auctionVolume925Max) }}</span>
          </template>
        </el-table-column>
        <el-table-column min-width="88" align="center">
          <template slot="header">
            <div class="th-stack">
              <div>今日31分</div>
              <div>最大量</div>
            </div>
          </template>
          <template slot-scope="{ row }">{{ fmtVolumeWan(row.volume931Today) }}</template>
        </el-table-column>
        <el-table-column min-width="88" align="center">
          <template slot="header">
            <div class="th-stack">
              <div>历史</div>
              <div>31分最大量</div>
            </div>
          </template>
          <template slot-scope="{ row }">{{ fmtVolumeWan(row.volume931Max) }}</template>
        </el-table-column>
        <el-table-column
          prop="carryThresholdAmountWan"
          min-width="72"
          align="center"
          sortable
          :sort-method="sortCarryThreshold"
        >
          <template slot="header">
            <div class="th-stack" title="最低承接阈(万)：前序×(-0.55)">
              <div>最低</div>
              <div>承接阈</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span
              v-if="row.carryThresholdMissing"
              class="carry-th-missing"
              :title="carryThresholdTitle(row)"
            >缺数据</span>
            <span v-else-if="row.carryThresholdAmountWan != null" :title="carryThresholdTitle(row)">
              {{ fmtCarryThresholdWan(row.carryThresholdAmountWan) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="carryOptimalThresholdAmountWan"
          min-width="72"
          align="center"
          sortable
          :sort-method="sortCarryOptimalThreshold"
        >
          <template slot="header">
            <div class="th-stack carry-opt-header" title="较优承接阈(万)：前序×(-0.2)提前量">
              <div>较优</div>
              <div>承接阈</div>
              <div class="carry-opt-header-sub">*-0.2</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span
              v-if="row.carryOptimalThresholdMissing"
              class="carry-th-missing"
              :title="carryOptimalThresholdTitle(row)"
            >{{ row.carryOptimalThresholdReason || '缺数据' }}</span>
            <span
              v-else-if="row.carryOptimalThresholdAmountWan != null"
              class="carry-opt-th"
              :title="carryOptimalThresholdTitle(row)"
            >{{ fmtCarryThresholdWan(row.carryOptimalThresholdAmountWan) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="volumeThresholdDisplay"
          min-width="80"
          align="center"
          sortable
          :sort-method="sortVolumeThreshold"
        >
          <template slot="header">
            <div class="th-stack vol-th-header" title="量阈V3：[P×1.21,P×1.331] 区间max×0.8">
              <div>量阈</div>
              <div class="vol-th-header-sub">V3×0.8</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span
              v-if="row.volumeThresholdMissing"
              class="carry-th-missing"
              :title="volumeThresholdTitle(row)"
            >缺数据</span>
            <div v-else-if="row.volumeThresholdDisplay" class="vol-th-cell" :title="volumeThresholdTitle(row)">
              <div class="vol-th-main">{{ row.volumeThresholdDisplay }}</div>
              <div v-if="row.volumeThresholdMaxDate" class="vol-th-sub">{{ row.volumeThresholdMaxDate }}</div>
              <div
                v-if="row.volumeThresholdMaxDisplayW != null"
                class="vol-th-sub"
              >{{ row.volumeThresholdMaxDisplayW }}w×{{ row.volumeThresholdFactor != null ? row.volumeThresholdFactor : 0.8 }}</div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="nodeScore"
          label="节点分"
          min-width="60"
          align="center"
          sortable
          :sort-method="sortNodeScore"
        >
          <template slot-scope="{ row }">
            <span
              v-if="row.nodeScore != null"
              class="node-score"
              :class="{ 'node-score-hit': isNodeScoreHit(row.nodeScore) }"
              :style="nodeScoreColorStyle(row.nodeScore)"
              :title="nodeScoreTitle(row)"
            >{{ row.nodeScore }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="midChip"
          min-width="64"
          align="center"
          sortable
          :sort-method="sortMid"
        >
          <template slot="header">
            <div class="th-stack" title="中期筹码分">
              <div>中期</div>
              <div>筹码分</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span :class="chipHlClass(row.highlightMidChip)">{{ fmtChipPct(row.midChip) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="C/J" min-width="72" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.cLevel" size="mini" type="warning">{{ row.cLevel }}</el-tag>
            <el-tag v-if="row.jLevel" size="mini" type="danger" style="margin-left: 2px">{{ row.jLevel }}</el-tag>
            <span v-if="!row.cLevel && !row.jLevel">-</span>
          </template>
        </el-table-column>
        <el-table-column label="竞价分" min-width="64" align="center" sortable :sort-method="sortKill">
          <template slot-scope="{ row }">
            <span :class="tierClass(killTier(row.auctionKillScore))">{{ fmtNum(row.auctionKillScore) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="竞涨幅" min-width="64" align="center" sortable :sort-method="sortGain">
          <template slot-scope="{ row }">{{ fmtNum(row.auctionGainPct) }}</template>
        </el-table-column>
        <el-table-column label="竞实换" min-width="64" align="center" sortable :sort-method="sortTurnover">
          <template slot-scope="{ row }">
            <span :class="tierClass(turnoverTier(row.auctionTurnoverRate))">{{ fmtNum(row.auctionTurnoverRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="竞量比" min-width="64" align="center" sortable :sort-method="sortVol">
          <template slot-scope="{ row }">
            <span :class="tierClass(volTier(row.auctionVolumeRatio))">{{ fmtNum(row.auctionVolumeRatio) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="昨板" min-width="48" align="center">
          <template slot-scope="{ row }">
            {{ row.yesterdayBoardsLabel || row.yesterdayBoards || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="筹码分" min-width="64" align="center" sortable :sort-method="sortChipSum">
          <template slot-scope="{ row }">
            <span :class="chipHlClass(row.highlightChipSum)">{{ fmtChipPct(row.chipScoreSum) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="短期" min-width="56" align="center" sortable :sort-method="sortShort">
          <template slot-scope="{ row }">
            <span :class="chipHlClass(row.highlightShortChip)">{{ fmtChipPct(row.shortChip) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="长期" min-width="56" align="center" sortable :sort-method="sortLong">
          <template slot-scope="{ row }">
            <span :class="chipHlClass(row.highlightLongChip)">{{ fmtChipPct(row.longChip) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="首板流入" min-width="72" align="center" sortable :sort-method="sortInflow">
          <template slot-scope="{ row }">
            <span :class="tierClass(inflowTier(row.netInflowToAmount0))">{{ fmtInflowPct(row.netInflowToAmount0) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      :title="libDialogTitle"
      :visible.sync="libVisible"
      width="520px"
      append-to-body
    >
      <el-table
        v-loading="libLoading"
        :data="libRows"
        border
        size="small"
        max-height="420"
        empty-text="昨日TOP库为空"
      >
        <el-table-column type="index" label="#" width="50" />
        <el-table-column prop="name" label="股票名称" min-width="120">
          <template slot-scope="{ row }">
            <div>{{ row.name || '-' }}</div>
            <div class="code-sub">{{ row.code }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="boards" label="板位" width="80" align="center">
          <template slot-scope="{ row }">{{ row.boards != null ? row.boards : '-' }}</template>
        </el-table-column>
        <el-table-column prop="tag" label="标签" width="70" align="center">
          <template slot-scope="{ row }">{{ row.tag || '-' }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
    <div
      v-show="auctionMaxTip.show"
      class="auction-max-pop"
      :style="{ left: auctionMaxTip.x + 'px', top: auctionMaxTip.y + 'px' }"
    >{{ auctionMaxTip.text }}</div>
  </div>
</template>

<script>
import stockApi from '@/api/stockApi';
import { formatGapUpLogicDisplay, hasGapUpLogicMatch } from '@/utils/gapUpLogicMatch';

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normCode6(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6);
}

function lookupLogicByCode(map, code) {
  if (!map || !code) return null;
  const raw = String(code || '').trim();
  if (map[raw]) return map[raw];
  const short = normCode6(raw);
  if (map[short]) return map[short];
  for (const k of Object.keys(map)) {
    if (normCode6(k) === short) return map[k];
  }
  return null;
}

export default {
  name: 'AuctionTop',
  data() {
    return {
      tradeDate: todayYmd(),
      prevDate: '',
      filterMa250: true,
      filterSlope: true,
      filterChipTier: '',
      filterBoardLevels: [],
      filterBaoTouch: false,
      filterCarry2bFloor: false,
      filterCarry3bFloor: false,
      loading: false,
      nextDayLoading: false,
      rows: [],
      auctionMaxTip: { show: false, text: '', x: 0, y: 0 },
      /** 上一交易日补涨「逻辑」匹配（按 code） */
      logicMatchByCode: {},
      logicSourceDate: '',
      /** 匹配股当日竞价涨幅 %，短代码 -> number */
      logicAuctionGainByCode: {},
      libVisible: false,
      libLoading: false,
      libDate: '',
      libRows: []
    };
  },
  computed: {
    libDialogTitle() {
      return this.libDate ? `昨日TOP库（${this.libDate}）` : '昨日TOP库';
    },
    logicSourceTip() {
      return this.logicSourceDate
        ? `复用上一交易日 ${this.logicSourceDate} 补涨逻辑`
        : '复用上一交易日补涨逻辑';
    }
  },
  methods: {
    getRowLogicMatch(row) {
      return lookupLogicByCode(this.logicMatchByCode, row && (row.code || row.ts_code));
    },
    hasLogicMatch(row) {
      return hasGapUpLogicMatch(this.getRowLogicMatch(row));
    },
    formatLogicDisplay(row) {
      return formatGapUpLogicDisplay(this.getRowLogicMatch(row));
    },
    lookupLogicAuctionGain(code) {
      if (code == null || code === '') return null;
      const raw = String(code).trim();
      if (Object.prototype.hasOwnProperty.call(this.logicAuctionGainByCode, raw)) {
        return this.logicAuctionGainByCode[raw];
      }
      const short = normCode6(raw);
      if (Object.prototype.hasOwnProperty.call(this.logicAuctionGainByCode, short)) {
        return this.logicAuctionGainByCode[short];
      }
      return null;
    },
    getLogicLines(row) {
      const logic = this.getRowLogicMatch(row);
      if (!logic) return [];
      if (logic.type === 'switch') return [{ kind: 'switch' }];
      if (logic.type !== 'match' || !Array.isArray(logic.items) || !logic.items.length) return [];
      return logic.items.map((item) => {
        const name = (item && item.name) || (item && item.code) || '';
        const score = item && item.conceptScore != null ? item.conceptScore : null;
        const label = score != null && score !== '' ? `${name}(${score})` : name;
        const gain = this.lookupLogicAuctionGain(item && item.code);
        return { kind: 'match', label, gain };
      });
    },
    formatLogicGain(gain) {
      const n = Number(gain);
      if (!Number.isFinite(n)) return '';
      const rounded = Math.round(n * 100) / 100;
      const sign = rounded > 0 ? '+' : '';
      return `${sign}${rounded}%`;
    },
    logicGainClass(gain) {
      const n = Number(gain);
      if (!Number.isFinite(n)) return '';
      if (n > 6) return 'logic-cell__gain--hot';
      if (n < 0) return 'logic-cell__gain--down';
      return 'logic-cell__gain--up';
    },
    collectLogicMatchCodes() {
      const codes = new Set();
      const map = this.logicMatchByCode || {};
      for (const entry of Object.values(map)) {
        if (!entry || entry.type !== 'match' || !Array.isArray(entry.items)) continue;
        for (const item of entry.items) {
          const c = normCode6(item && item.code);
          if (c && c !== '000000') codes.add(c);
        }
      }
      return [...codes];
    },
    async loadLogicMatchAuctionGains(tradeDate) {
      this.logicAuctionGainByCode = {};
      const codes = this.collectLogicMatchCodes();
      if (!tradeDate || !codes.length) return;
      try {
        const res = await stockApi.fetchMatchAuctionGains(tradeDate, codes);
        if (res && res.ok && res.byCode) {
          this.logicAuctionGainByCode = res.byCode;
        }
      } catch (_e) {
        this.logicAuctionGainByCode = {};
      }
    },
    async loadPrevDayLogic(prevDate) {
      this.logicMatchByCode = {};
      this.logicSourceDate = '';
      this.logicAuctionGainByCode = {};
      if (!prevDate) return;
      try {
        const res = await stockApi.fetchGapUpLogicMatch(prevDate);
        if (res && res.ok) {
          this.logicMatchByCode = res.logicMatchByCode || {};
          this.logicSourceDate = res.tradeDate || prevDate;
        }
      } catch (_e) {
        this.logicMatchByCode = {};
        this.logicSourceDate = '';
      }
    },
    fmtNum(v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return String(Math.round(n * 100) / 100);
    },
    bothBurst(row) {
      return row && row.auctionVolumeBurst === '爆' && row.volume931Burst === '31分爆';
    },
    auctionMaxDateTitle(row) {
      const date = row && row.auctionVolume925MaxDate;
      return date ? `出现在 ${date}` : '';
    },
    showAuctionMaxTip(event, row) {
      const text = this.auctionMaxDateTitle(row);
      if (!text) return;
      const rect = event.currentTarget.getBoundingClientRect();
      this.auctionMaxTip = {
        show: true,
        text,
        x: rect.left + rect.width / 2,
        y: rect.top - 6
      };
    },
    hideAuctionMaxTip() {
      this.auctionMaxTip.show = false;
    },
    fmtVolumeWan(v) {
      if (v == null || v === '') return '';
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '';
      return (n / 10000).toFixed(1);
    },
    fmtCarryThresholdWan(v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return String(Math.round(n));
    },
    fmtChipPct(v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return `${Math.round(n * 10000) / 100}%`;
    },
    fmtInflowPct(v) {
      if (v == null || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      return `${Math.round(n * 10000) / 100}%`;
    },
    turnoverTier(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      if (n >= 3) return 'pink';
      if (n >= 2) return 'red';
      if (n >= 1) return 'orange';
      return '';
    },
    volTier(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      if (n >= 70) return 'pink';
      if (n >= 40) return 'red';
      if (n >= 20) return 'orange';
      return '';
    },
    killTier(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      if (n >= 20) return 'pink';
      if (n >= 10) return 'red';
      if (n >= 5) return 'orange';
      return '';
    },
    inflowTier(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      if (n >= 0.35) return 'pink';
      if (n >= 0.2) return 'red';
      return '';
    },
    tierClass(tier) {
      if (tier === 'pink') return 'hl-pink';
      if (tier === 'red') return 'hl-red';
      if (tier === 'orange') return 'hl-orange';
      return '';
    },
    /** 筹码：TOP1 粉 / TOP2~3 红 */
    chipHlClass(tier) {
      if (tier === 'pink') return 'hl-pink';
      if (tier === 'red') return 'hl-red';
      return '';
    },
    topFlagsText(n) {
      const count = Number(n) || 0;
      if (count <= 0) return '';
      return '🚩'.repeat(Math.min(count, 2));
    },
    hasNodeCore(row) {
      return !!(row && (row.nodeCore || row.nodeHit));
    },
    /** 节点分≥7 为达标强色段 */
    isNodeScoreHit(score) {
      const s = Number(score);
      return Number.isFinite(s) && s >= 7;
    },
    /**
     * 方案A：青系渐变。从3分极淡起色；[3,7)弱段；≥7强色起跳(#13c2c2脉)，至22封顶加深。
     * 名称与节点分同色。
     */
    nodeScoreColor(score) {
      const s = Number(score);
      if (!Number.isFinite(s) || s < 3) return null;
      const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
      const lerp = (a, b, t) => a + (b - a) * t;
      if (s < 7) {
        const t = (s - 3) / 4;
        const sat = Math.round(lerp(22, 46, t));
        const light = Math.round(lerp(78, 58, t));
        return `hsl(180, ${sat}%, ${light}%)`;
      }
      const t = clamp((s - 7) / 15, 0, 1);
      const sat = Math.round(lerp(82, 88, t));
      const light = Math.round(lerp(42, 24, t));
      return `hsl(180, ${sat}%, ${light}%)`;
    },
    nodeScoreColorStyle(score) {
      const c = this.nodeScoreColor(score);
      return c ? { color: c } : null;
    },
    nodeScoreTitle(row) {
      const hits = Array.isArray(row.nodeHits) ? row.nodeHits.join('、') : '';
      const fb = row.firstBoardDate || '';
      const parts = [];
      if (fb) parts.push(`首板日 ${fb}`);
      if (hits) parts.push(`命中 ${hits}`);
      if (row.nodeHit) parts.push('达标≥7');
      return parts.join(' · ') || '节点分';
    },
    starText(tier) {
      if (tier === 'pink5') return '★★★★★';
      if (tier === 'blue3') return '★★★';
      return '';
    },
    starClass(tier) {
      if (tier === 'pink5') return 'stars-pink';
      if (tier === 'blue3') return 'stars-blue';
      return '';
    },
    sortByField(field) {
      return (a, b) => {
        const av = a[field] == null ? -Infinity : Number(a[field]);
        const bv = b[field] == null ? -Infinity : Number(b[field]);
        return av - bv;
      };
    },
    sortTotal(a, b) {
      return this.sortByField('totalScore')(a, b);
    },
    sortCarryThreshold(a, b) {
      const av =
        a.carryThresholdMissing || a.carryThresholdAmountWan == null
          ? -Infinity
          : Number(a.carryThresholdAmountWan);
      const bv =
        b.carryThresholdMissing || b.carryThresholdAmountWan == null
          ? -Infinity
          : Number(b.carryThresholdAmountWan);
      return av - bv;
    },
    sortCarryOptimalThreshold(a, b) {
      const av =
        a.carryOptimalThresholdMissing || a.carryOptimalThresholdAmountWan == null
          ? -Infinity
          : Number(a.carryOptimalThresholdAmountWan);
      const bv =
        b.carryOptimalThresholdMissing || b.carryOptimalThresholdAmountWan == null
          ? -Infinity
          : Number(b.carryOptimalThresholdAmountWan);
      return av - bv;
    },
    yesterdayBoardGlyph(row) {
      const b = Number(row.yesterdayBoards);
      if (b === 1) return '一';
      if (b === 2) return '二';
      if (b === 3) return '三';
      return '';
    },
    yesterdayBoardGlyphClass(row) {
      const b = Number(row.yesterdayBoards);
      if (b === 1) return 'board-glyph--1';
      if (b === 2) return 'board-glyph--2';
      if (b === 3) return 'board-glyph--3';
      return '';
    },
    carryThresholdTitle(row) {
      const b = Number(row.yesterdayBoards);
      if (b === 1) return '1B净流入×(-0.55)，单位万元';
      if (b === 2) return '(1B+2B)净流入×(-0.55)，单位万元';
      if (b === 3) return '(1B+2B+3B)净流入×(-0.55)，单位万元';
      return '大单承接比阈值金额(万)';
    },
    carryOptimalThresholdTitle(row) {
      if (row.carryOptimalThresholdReason) return row.carryOptimalThresholdReason;
      const b = Number(row.yesterdayBoards);
      if (b === 1) return '1B净流入×(-0.2)（提前量），单位万元';
      if (b === 2) return '(1B+2B)净流入×(-0.2)（提前量），单位万元';
      if (b === 3) return '(1B+2B+3B)净流入×(-0.2)（提前量），单位万元';
      return '较优承接阈：前序累计×(-0.2)（提前量）';
    },
    volumeThresholdTitle(row) {
      const src = row.volumeThresholdSource === 'histMax' ? '无区间命中，取历史max' : '区间内max';
      const factor = row.volumeThresholdFactor != null ? row.volumeThresholdFactor : 0.8;
      const base = row.volumeThresholdMaxDate
        ? `${row.volumeThresholdMaxDate} 成交量×${factor}`
        : `成交量阈值(max×${factor})`;
      const p = row.firstBoardPrice;
      const lo = row.volumeThresholdPriceLo != null ? row.volumeThresholdPriceLo : p != null ? +(p * 1.21).toFixed(2) : null;
      const hi = row.volumeThresholdPriceHi != null ? row.volumeThresholdPriceHi : p != null ? +(p * 1.331).toFixed(2) : null;
      if (lo != null && hi != null) return `${base}；${src}；量阈V3 区间 [${lo}, ${hi}]`;
      return `${base}；量阈V3×${factor}`;
    },
    sortVolumeThreshold(a, b) {
      const av =
        a.volumeThresholdMissing || a.volumeThresholdVolume == null
          ? -Infinity
          : Number(a.volumeThresholdVolume);
      const bv =
        b.volumeThresholdMissing || b.volumeThresholdVolume == null
          ? -Infinity
          : Number(b.volumeThresholdVolume);
      return av - bv;
    },
    sortNodeScore(a, b) {
      return this.sortByField('nodeScore')(a, b);
    },
    sortKill(a, b) {
      return this.sortByField('auctionKillScore')(a, b);
    },
    sortGain(a, b) {
      return this.sortByField('auctionGainPct')(a, b);
    },
    sortTurnover(a, b) {
      return this.sortByField('auctionTurnoverRate')(a, b);
    },
    sortVol(a, b) {
      return this.sortByField('auctionVolumeRatio')(a, b);
    },
    sortChipSum(a, b) {
      return this.sortByField('chipScoreSum')(a, b);
    },
    sortShort(a, b) {
      return this.sortByField('shortChip')(a, b);
    },
    sortMid(a, b) {
      return this.sortByField('midChip')(a, b);
    },
    sortLong(a, b) {
      return this.sortByField('longChip')(a, b);
    },
    sortInflow(a, b) {
      return this.sortByField('netInflowToAmount0')(a, b);
    },
    async handleNextTradeDay() {
      if (!this.tradeDate) {
        this.$message.warning('请选择日期');
        return;
      }
      this.nextDayLoading = true;
      try {
        const res = await stockApi.nextHistoryFirstBoardTradeDate(this.tradeDate);
        if (!res?.ok || !res.nextTradeDate) {
          this.$message.warning(res?.message || '无下一交易日');
          return;
        }
        this.tradeDate = res.nextTradeDate;
        await this.handleQuery();
      } catch (err) {
        this.$message.error(err?.message || '获取下一交易日失败');
      } finally {
        this.nextDayLoading = false;
      }
    },
    async handleQuery() {
      if (!this.tradeDate) {
        this.$message.warning('请选择日期');
        return;
      }
      this.loading = true;
      try {
        const res = await stockApi.queryAuctionTop({
          tradeDate: this.tradeDate,
          filterMa250: this.filterMa250,
          filterSlope: this.filterSlope,
          filterChipTier: this.filterChipTier || undefined,
          filterBoardLevels: this.filterBoardLevels.length ? this.filterBoardLevels : undefined,
          filterBaoTouch: this.filterBaoTouch,
          filterCarry2bFloor: this.filterCarry2bFloor,
          filterCarry3bFloor: this.filterCarry3bFloor
        });
        this.prevDate = res?.prevDate || '';
        this.rows = Array.isArray(res?.rows) ? res.rows : [];
        await this.loadPrevDayLogic(this.prevDate);
        await this.loadLogicMatchAuctionGains(this.tradeDate);
        if (res?.message && !this.rows.length) {
          this.$message.info(res.message);
        }
      } catch (err) {
        this.rows = [];
        this.logicMatchByCode = {};
        this.logicSourceDate = '';
        this.logicAuctionGainByCode = {};
        this.$message.error(err?.message || '查询失败');
      } finally {
        this.loading = false;
      }
    },
    async openYesterdayTopLib() {
      if (!this.tradeDate) {
        this.$message.warning('请选择日期');
        return;
      }
      this.libVisible = true;
      this.libLoading = true;
      this.libRows = [];
      this.libDate = '';
      try {
        // 相对「所选日期」解析：竞价交易日 → 上一交易日 TOP 库（与列表★★★同源）
        const res = await stockApi.fetchAuctionTopLibraryPrevOf(this.tradeDate);
        this.libDate = res?.tradeDate || '';
        if (this.libDate) this.prevDate = this.libDate;
        this.libRows = Array.isArray(res?.rows) ? res.rows : [];
        if (res?.message && !this.libRows.length) {
          this.$message.info(res.message);
        }
      } catch (err) {
        this.libRows = [];
        this.$message.error(err?.message || '加载昨日TOP库失败');
      } finally {
        this.libLoading = false;
      }
    }
  },
  watch: {
    filterMa250() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterSlope() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterChipTier() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterBoardLevels() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterBaoTouch() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterCarry2bFloor() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    },
    filterCarry3bFloor() {
      if (this.rows.length || this.prevDate) this.handleQuery();
    }
  }
};
</script>

<style scoped>
.auction-top {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  padding: 8px 10px;
  box-sizing: border-box;
  overflow: hidden;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}
.auction-top-table >>> .el-table .cell {
  padding-left: 2px;
  padding-right: 2px;
  line-height: 1.25;
}
.auction-top-table >>> .el-table th > .cell {
  padding-left: 1px;
  padding-right: 1px;
  line-height: 1.15;
}
.auction-top-table >>> .el-table td,
.auction-top-table >>> .el-table th {
  padding: 2px 0;
}
.th-stack {
  line-height: 1.15;
  text-align: center;
  font-size: 12px;
}
.meta-tip {
  color: #909399;
  font-size: 12px;
}
.code-sub {
  font-size: 10px;
  color: #909399;
  line-height: 1.1;
}
.auction-top-table >>> td.auction-logic-col .cell {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.3;
}
.logic-cell {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
}
.logic-cell__lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.logic-cell__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  line-height: 1.35;
}
.logic-cell__text {
  font-size: 12px;
  color: #303133;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
}
.logic-cell__gain {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  padding: 0 3px;
  border-radius: 2px;
}
.logic-cell__gain--up {
  color: #f56c6c;
  background: transparent;
}
.logic-cell__gain--hot {
  color: #fff;
  background: #f56c6c;
}
.logic-cell__gain--down {
  color: #fff;
  background: #67c23a;
}
.burst-tags {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  white-space: nowrap;
}
.burst-tags.is-both {
  padding: 1px 2px;
  border-radius: 4px;
  background: rgba(255, 45, 140, 0.16);
}
.auction-burst {
  display: inline-block;
  padding: 0 4px;
  background: #e10600;
  color: #fff;
  font-weight: 700;
  line-height: 18px;
  border-radius: 2px;
}
.auction-burst.is-both {
  background: #ff2d8a;
  color: #fff;
  animation: burst-both-pop 1.1s ease-in-out infinite;
}
@keyframes burst-both-pop {
  0%, 100% {
    box-shadow: 0 0 0 1px #fff, 0 0 6px #ff2d8a;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 1px #fff, 0 0 16px #ff2d8a;
    transform: scale(1.08);
  }
}
.logic-cell__dot {
  flex-shrink: 0;
  margin-top: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #409eff;
}
.yesterday-top-stars {
  color: #ff4d4f;
  font-weight: 700;
  letter-spacing: 1px;
}
.top-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.05;
  gap: 1px;
}
.top-flags {
  font-size: 12px;
  letter-spacing: 0;
  line-height: 1;
}
.stars-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.stars-row {
  letter-spacing: -1px;
  line-height: 1;
  white-space: nowrap;
}
.stars-pink {
  color: #ff69b4;
  font-weight: 700;
  font-size: 11px;
}
.stars-blue {
  color: #409eff;
  font-weight: 700;
  font-size: 11px;
}
.node-mark {
  display: inline-block;
  margin-top: 0;
  color: #13c2c2;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 0 0.5px #08979c;
}
.node-score {
  font-variant-numeric: tabular-nums;
}
.node-score-hit {
  font-weight: 700;
}
.name-line {
  display: flex;
  align-items: baseline;
  white-space: nowrap;
}
.stock-name {
  line-height: 1.15;
  font-size: 12px;
}
.board-glyph {
  margin-left: 1px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.15;
  flex-shrink: 0;
}
.board-glyph--1 {
  color: #e10600;
}
.board-glyph--2 {
  color: #722ed1;
}
.board-glyph--3 {
  color: #389e0d;
}
.stock-name-hit {
  font-weight: 700;
}
.carry-th-missing {
  color: #909399;
  font-size: 11px;
}
.carry-opt-th {
  color: #ff4d4f;
  font-weight: 700;
}
.carry-opt-header {
  line-height: 1.15;
  text-align: center;
}
.carry-opt-header-sub {
  margin-top: 1px;
  font-size: 10px;
  font-weight: 700;
  color: #ff4d4f;
}
.vol-th-cell {
  line-height: 1.15;
  white-space: normal;
  word-break: break-all;
}
.vol-th-main {
  font-weight: 600;
}
.vol-th-sub {
  margin-top: 1px;
  font-size: 9px;
  color: #909399;
  white-space: normal;
  line-height: 1.1;
}
.vol-th-header {
  line-height: 1.15;
  text-align: center;
}
.vol-th-header-sub {
  margin-top: 1px;
  font-size: 10px;
  color: #909399;
}
.vol-boost-mark {
  display: none;
}
.hl-pink {
  color: #ff69b4;
  font-weight: 700;
}
.hl-red {
  color: #ff4d4f;
  font-weight: 700;
}
.hl-orange {
  color: #fa8c16;
  font-weight: 700;
}
.auction-top-table {
  width: 100%;
}
.auction-top-table >>> .el-table {
  width: 100% !important;
}
.auction-top-table >>> .el-table__header-wrapper table,
.auction-top-table >>> .el-table__body-wrapper table,
.auction-top-table >>> .el-table__footer-wrapper table {
  width: 100% !important;
}
.auction-top-table >>> .el-table__header th .cell {
  white-space: nowrap;
}
.auction-top-table >>> .caret-wrapper {
  display: none;
}
.auction-max-pop {
  position: fixed;
  z-index: 4000;
  transform: translate(-50%, -100%);
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  white-space: nowrap;
  border-radius: 2px;
  pointer-events: none;
}
</style>
